import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = `
**Última actualización: Enero 2026**

## 1. Información General

El Protocolo Reinicio 40+ ("nosotros", "nuestro") se compromete a proteger la privacidad de los usuarios ("tú", "tu") que visitan nuestro sitio web y adquieren nuestros productos digitales. Esta Política de Privacidad describe cómo recopilamos, usamos y protegemos tu información personal.

## 2. Información que Recopilamos

Recopilamos la siguiente información cuando realizas una compra:
- Nombre completo
- Dirección de correo electrónico
- Información de pago (procesada de forma segura por nuestro procesador de pagos externo; no almacenamos datos de tarjetas)
- País de residencia

También podemos recopilar automáticamente:
- Dirección IP
- Tipo de navegador y dispositivo
- Páginas visitadas y tiempo de permanencia
- Cookies y tecnologías similares

## 3. Uso de la Información

Utilizamos tu información personal para:
- Procesar y entregar tu compra
- Enviar el material digital adquirido
- Proporcionar soporte al cliente
- Enviar comunicaciones relacionadas con tu compra
- Mejorar nuestros productos y servicios
- Cumplir con obligaciones legales

## 4. Protección de Datos

Implementamos medidas de seguridad técnicas y organizativas para proteger tu información personal, incluyendo:
- Encriptación SSL en todas las transacciones
- Almacenamiento seguro de datos
- Acceso restringido a información personal
- Revisiones periódicas de seguridad

## 5. Compartir Información

No vendemos, alquilamos ni compartimos tu información personal con terceros, excepto:
- Procesadores de pago necesarios para completar tu transacción
- Proveedores de servicios que nos ayudan a operar nuestro negocio
- Cuando sea requerido por ley

## 6. Cookies

Nuestro sitio web utiliza cookies para:
- Mantener tu sesión activa
- Recordar tus preferencias
- Analizar el tráfico del sitio
- Mejorar la experiencia del usuario

Puedes configurar tu navegador para rechazar cookies, aunque esto puede afectar la funcionalidad del sitio.

## 7. Tus Derechos

Tienes derecho a:
- Acceder a tu información personal
- Solicitar la corrección de datos inexactos
- Solicitar la eliminación de tus datos
- Oponerte al procesamiento de tus datos
- Solicitar la portabilidad de tus datos

## 8. Retención de Datos

Conservamos tu información personal durante el tiempo necesario para cumplir con los fines descritos en esta política, a menos que la ley exija un período de retención más largo.

## 9. Menores de Edad

Nuestros productos están dirigidos a personas mayores de 18 años. No recopilamos intencionalmente información de menores de edad.

## 10. Cambios a Esta Política

Nos reservamos el derecho de actualizar esta Política de Privacidad en cualquier momento. Los cambios serán publicados en esta página con la fecha de actualización correspondiente.
`.trim();
const termsContent = `
**Última actualización: Enero 2026**

## 1. Aceptación de los Términos

Al acceder a este sitio web y/o adquirir "El Protocolo Reinicio 40+" ("el Producto"), aceptas estar sujeto a estos Términos y Condiciones. Si no estás de acuerdo con alguna parte de estos términos, no debes utilizar nuestro sitio ni adquirir el Producto.

## 2. Descripción del Producto

El Protocolo Reinicio 40+ es un producto digital informativo que incluye:
- Un ebook principal en formato PDF
- Materiales complementarios (bonuses) en formato PDF
- Todo el contenido es descargable e inmediatamente accesible tras la compra

## 3. Naturaleza Informativa

El Producto es exclusivamente informativo y educativo. **No constituye consejo médico, diagnóstico ni tratamiento.** La información proporcionada no sustituye la consulta con profesionales de la salud calificados. Siempre consulta a tu médico antes de realizar cambios significativos en tu alimentación o estilo de vida.

## 4. Precio y Pago

- El precio del Producto es de $6.36 USD (pago único)
- No existen suscripciones ni cargos recurrentes
- El pago se procesa de forma segura a través de procesadores de pago certificados
- Los precios pueden estar sujetos a cambios sin previo aviso para futuras compras

## 5. Entrega del Producto

- Tras completar el pago, recibirás acceso inmediato al material digital
- Los enlaces de descarga se enviarán a la dirección de correo electrónico proporcionada
- Es tu responsabilidad proporcionar una dirección de correo electrónico válida y activa

## 6. Garantía de Devolución

- Ofrecemos una garantía de satisfacción de 7 días naturales a partir de la fecha de compra
- Si no estás satisfecha con el Producto, puedes solicitar un reembolso completo dentro de este período
- El reembolso se procesará utilizando el mismo método de pago original
- Tras el reembolso, perderás acceso al material digital

## 7. Propiedad Intelectual

- Todo el contenido del Producto está protegido por derechos de autor
- Al adquirir el Producto, obtienes una licencia personal e intransferible para uso individual
- Queda estrictamente prohibido:
  - Reproducir, distribuir o compartir el contenido
  - Modificar o crear obras derivadas
  - Usar el contenido con fines comerciales
  - Revender o sublicenciar el material

## 8. Resultados

- Los resultados individuales pueden variar significativamente
- No garantizamos resultados específicos de salud, pérdida de peso ni ningún otro resultado
- Los testimonios presentados representan experiencias individuales y no garantizan resultados similares
- Los factores como la genética, el estado de salud, la adherencia al programa y otros factores individuales influyen en los resultados

## 9. Limitación de Responsabilidad

- El Producto se proporciona "tal cual" sin garantías de ningún tipo
- No nos hacemos responsables de daños directos, indirectos, incidentales o consecuentes derivados del uso del Producto
- No somos responsables de decisiones de salud tomadas basándose en la información del Producto
- Nuestra responsabilidad máxima se limita al precio pagado por el Producto

## 10. Uso Aceptable

Al adquirir el Producto, te comprometes a:
- Usar el material exclusivamente para fines personales
- No compartir tus credenciales de acceso
- No intentar eludir las medidas de protección del contenido
- Consultar a un profesional de la salud antes de implementar cambios

## 11. Modificaciones

Nos reservamos el derecho de modificar estos Términos y Condiciones en cualquier momento. Las modificaciones entrarán en vigor inmediatamente después de su publicación en el sitio web.

## 12. Legislación Aplicable

Estos Términos y Condiciones se regirán e interpretarán de acuerdo con las leyes aplicables. Cualquier disputa será resuelta en los tribunales competentes.

## 13. Divisibilidad

Si alguna disposición de estos Términos se considera inválida o inaplicable, las disposiciones restantes continuarán en pleno vigor y efecto.
`.trim();
function renderMarkdown(text: string) {
  return text.split('\n').map((line, i) => {
    if (line.startsWith('## ')) {
      return <h3 key={i} className="text-[17px] font-bold text-dark mt-6 mb-2" data-id="element-88">
          {line.replace('## ', '')}
        </h3>;
    }
    if (line.startsWith('**') && line.endsWith('**')) {
      return <p key={i} className="text-[14px] font-bold text-[#555] mb-3" data-id="element-89">
          {line.replace(/\*\*/g, '')}
        </p>;
    }
    if (line.startsWith('- ')) {
      return <li key={i} className="text-[14px] text-[#555] leading-[1.8] ml-4 list-disc" data-id="element-90">
          {line.replace('- ', '').split('**').map((part, j) => j % 2 === 1 ? <strong key={j} data-id="element-91">{part}</strong> : part)}
        </li>;
    }
    if (line.trim() === '') {
      return <div key={i} className="h-2" data-id="element-92" />;
    }
    return <p key={i} className="text-[14px] text-[#555] leading-[1.8]" data-id="element-93">
        {line.split('**').map((part, j) => j % 2 === 1 ? <strong key={j} data-id="element-94">{part}</strong> : part)}
      </p>;
  });
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="w-full bg-dark py-10 px-4" data-id="element-95">
        <div className="max-w-[960px] mx-auto flex flex-col items-center text-center" data-id="element-96">
          <div className="text-teal font-bold text-[18px] mb-6 tracking-wide" data-id="element-97">
            REINICIO 40+
          </div>

          <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-[13px] text-white/60 mb-6" data-id="element-98">
            <button onClick={() => setActiveModal('privacy')} className="hover:text-white transition-colors cursor-pointer" data-id="element-99">
              Política de Privacidad
            </button>
            <span className="hidden sm:inline" data-id="element-100">|</span>
            <button onClick={() => setActiveModal('terms')} className="hover:text-white transition-colors cursor-pointer" data-id="element-101">
              Términos y Condiciones
            </button>
          </div>

          <div className="w-full h-px bg-white/10 mb-6" data-id="element-102"></div>

          <p className="text-[12px] md:text-[13px] text-white/50 leading-relaxed max-w-3xl mb-6" data-id="element-103">
            Este producto es solo para fines informativos y no reemplaza el
            consejo médico profesional. Los resultados individuales pueden
            variar. Consulta a tu médico antes de realizar cambios
            significativos en tu alimentación.
          </p>

          <p className="text-[12px] md:text-[13px] text-white/40" data-id="element-104">
            © 2025 El Protocolo Reinicio 40+. Todos los derechos reservados.
          </p>
        </div>
      </footer>

      {/* Legal Modal */}
      <AnimatePresence data-id="element-105">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-106">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.25
        }} className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-107">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0" data-id="element-108">
                <h2 className="text-[20px] font-bold text-dark" data-id="element-109">
                  {activeModal === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
                </h2>
                <button onClick={() => setActiveModal(null)} className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors" data-id="element-110">
                  <X size={18} className="text-gray-600" data-id="element-111" />
                </button>
              </div>

              {/* Content */}
              <div className="overflow-y-auto px-6 py-5 flex-grow" data-id="element-112">
                {renderMarkdown(activeModal === 'privacy' ? privacyContent : termsContent)}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-100 flex-shrink-0" data-id="element-113">
                <button onClick={() => setActiveModal(null)} className="w-full bg-teal text-white font-bold py-3 rounded-lg hover:bg-teal-dark transition-colors text-[15px]" data-id="element-114">
                  Cerrar
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}